import { useEffect, useRef, useState } from "react";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import { useExperience } from "../hooks/useExperience";
import { useLocale } from "../i18n/LocaleContext";

function withAlpha(hex, alpha = 0.38) {
  const h = (hex || "#a1a1aa").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function Experience() {
  const { t } = useLocale();
  const experienceData = useExperience();

  return (
    <section id="experience" className="relative w-full pt-4 pb-12 md:pt-6 md:pb-16 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-6 md:px-16">
        <div className="flex flex-col items-center mb-10">
          <h2 className="text-sm font-semibold tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-400">
            {t("sections.experience")}
          </h2>
          <span className="mt-2 block h-px w-16 bg-gradient-to-r from-transparent via-zinc-400 dark:via-zinc-600 to-transparent" />
        </div>

        <div className="flex flex-col gap-6">
          {experienceData.companies.map((company, i) => (
            <CompanyCard key={company.company} company={company} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CompanyCard({ company, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const hasProjects = company.projects && company.projects.length > 0;
  const accent = withAlpha(company.color, 0.42);
  const accentSoft = withAlpha(company.color, 0.18);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s`,
      }}
      className="flex flex-col md:flex-row md:items-start gap-4 md:gap-5"
    >
      <aside
        className={`w-full md:w-[240px] shrink-0 ${
          hasProjects ? "md:pt-6" : "md:py-1"
        }`}
      >
        <header className="space-y-2.5">
          <a
            href={company.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-8 opacity-80 hover:opacity-100 transition-opacity"
            aria-label={company.company}
          >
            <img
              src={company.logo}
              alt=""
              className="h-full w-auto max-w-[128px] object-contain object-left"
              draggable="false"
            />
          </a>

          <div className="space-y-0.5">
            <h3 className="text-base md:text-lg font-bold text-zinc-900 dark:text-zinc-100 leading-snug">
              {company.role}
            </h3>

            <a
              href={company.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:underline hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              {company.company}
            </a>

            <p className="text-xs text-zinc-500 dark:text-zinc-400 tracking-wide">
              {company.startDate} – {company.endDate}
            </p>
          </div>
        </header>

        <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {company.summary}
        </p>
      </aside>

      {hasProjects && (
        <article
          className="flex-1 min-w-0 rounded-2xl bg-white/55 dark:bg-zinc-900/55 backdrop-blur-sm border border-zinc-200/50 dark:border-zinc-700/50 shadow-sm dark:shadow-black/20 overflow-hidden"
          style={{ borderLeftWidth: 3, borderLeftColor: accent }}
        >
          <div className="p-5 md:p-6 flex flex-col gap-5">
            {company.projects.map((project, j) => (
              <ProjectCaseStudy
                key={project.name}
                project={project}
                isLast={j === company.projects.length - 1}
                accentColor={accent}
                accentSoft={accentSoft}
              />
            ))}
          </div>
        </article>
      )}
    </div>
  );
}

function ProjectCaseStudy({ project, isLast, accentColor, accentSoft }) {
  const { t } = useLocale();
  const TitleTag = project.link ? "a" : "div";
  const titleProps = project.link
    ? {
        href: project.link,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <div
      className={`group ${!isLast ? "pb-5 border-b" : ""}`}
      style={!isLast ? { borderColor: accentSoft } : undefined}
    >
      <TitleTag
        {...titleProps}
        className="inline-flex items-center gap-2 mb-3"
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: accentColor }}
        />
        <h4 className="text-sm md:text-base font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 transition-colors">
          {project.name}
        </h4>
        {project.link && (
          <FaArrowUpRightFromSquare className="w-2.5 h-2.5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        )}
      </TitleTag>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs md:text-[13px] leading-relaxed">
        <CaseRow label={t("experience.problem")} value={project.problem} />
        <CaseRow label={t("experience.solution")} value={project.solution} />
        {project.impact && <CaseRow label={t("experience.impact")} value={project.impact} />}
      </dl>
    </div>
  );
}

function CaseRow({ label, value }) {
  return (
    <>
      <dt className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 dark:text-zinc-500 pt-0.5">
        {label}
      </dt>
      <dd className="text-zinc-700 dark:text-zinc-300">{value}</dd>
    </>
  );
}
